import type { TeamGroup } from '@/apis/teamGroups';
import theme from '@/assets/theme';

/**
 * Team groups in pickers. Choosing a group selects all of its teams that are
 * options in that picker; only team ids are ever stored or sent.
 */

/** Value prefix for a group row in a Select-based picker (never stored). */
export const GROUP_VALUE_PREFIX = 'team-group:';

export const toGroupValue = (groupId: string): string =>
  `${GROUP_VALUE_PREFIX}${groupId}`;

export const isGroupValue = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith(GROUP_VALUE_PREFIX);

export const groupIdFromValue = (value: string): string =>
  value.slice(GROUP_VALUE_PREFIX.length);

/**
 * Groups limited to the teams present in a picker's options. Groups with
 * no team in the picker are dropped.
 */
export function groupsForOptions(
  groups: TeamGroup[],
  optionIds: Iterable<string | number>
): TeamGroup[] {
  const available = new Set(Array.from(optionIds, String));
  return groups
    .filter((group) => Array.isArray(group?.team_ids))
    .map((group) => ({
      ...group,
      team_ids: group.team_ids.filter((id) => available.has(id)),
    }))
    .filter((group) => group.team_ids.length > 0);
}

export interface GroupSelectionState {
  checked: boolean;
  indeterminate: boolean;
}

/** Checked when every team is selected; indeterminate when only some are. */
export function groupSelectionState(
  group: TeamGroup,
  selected: Iterable<string | number>
): GroupSelectionState {
  const selectedIds = new Set(Array.from(selected, String));
  const count = group.team_ids.filter((id) => selectedIds.has(id)).length;
  return {
    checked: count > 0 && count === group.team_ids.length,
    indeterminate: count > 0 && count < group.team_ids.length,
  };
}

/**
 * Toggle a group: clear its teams when all are selected, otherwise add
 * the missing ones. Keeps the existing order and value types.
 */
export function toggleGroupSelection<T extends string | number>(
  group: TeamGroup,
  selected: T[]
): T[] {
  const { checked } = groupSelectionState(group, selected);
  const memberIds = new Set(group.team_ids);
  if (checked) {
    return selected.filter((value) => !memberIds.has(String(value)));
  }
  const selectedIds = new Set(selected.map(String));
  const missing = group.team_ids.filter((id) => !selectedIds.has(id));
  return [...selected, ...(missing as T[])];
}

/** Whether a group matches a search: its own name or any team's name. */
export function groupMatchesSearch(
  group: TeamGroup,
  search: string,
  memberName: (id: string) => string | undefined
): boolean {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  return (
    group.name.toLowerCase().includes(term) ||
    group.team_ids.some((id) =>
      (memberName(id) ?? '').toLowerCase().includes(term)
    )
  );
}

/**
 * Section headings for pickers that show groups: one above the groups and
 * one above the normal teams, so the two lists don't run together. Follows the
 * theme's team wording ("Clients" in the Cardinal theme).
 */
export function teamPickerSectionLabels(): { groups: string; teams: string } {
  return theme.themeType === 2
    ? { groups: 'Groups', teams: 'Clients' }
    : { groups: 'Groups', teams: 'Teams' };
}
